import { type MutableRefObject, useEffect } from 'react'
import type { Client } from 'braintree-web'
import { useSelector } from 'react-redux'
import type {
  ContactDetails,
  CreditCardDepositRequest,
  DepositResponse,
  InitialData,
  PaymentMethod,
} from '@mobi/api-types'
import type { PaymentMethods } from '@mobi/component-library/Areas/Deposit/types'
import { addCrumb, coerceIntoError, unwrapErrorMessage } from '@mobi/utils'
import { type HostApi, useHostContext } from '../../../../Common/HostContext'
import { useUserContactDetails } from '../../../../Hooks/useUserContactDetails'
import { track } from '../../../../Utils/analytics'
import { reportErrorIfNeeded } from '../../../../Utils/sentry'
import { DepositError } from '../../Components'
import { useDeposit } from '../../Hooks/useDeposit'
import { selectDepositFlow } from '../../Store'
import { ThreeDSecureError, generatePaymentNonce, verifyThreeDSecure } from '../../Utils'
import {
  logLiabilityShiftError,
  logLiabilityShiftRejected,
  logLiabilityShiftSuccess,
} from '../../Utils/logging'
import type { DepositFlow, UserCancelable } from '../../typings/types'

type Props = {
  braintreeClient: MutableRefObject<Client | undefined>
  accountNumber: number
  mustUse3DSecure: boolean
  initialData: InitialData
  onDepositing: VoidFunction
}

type DepositProps = Pick<DepositParams, 'creditCard'> & {
  isUsingNewCard: boolean
  depositAmount: number
}

export function useBankCardDeposit({
  mustUse3DSecure,
  accountNumber,
  initialData,
  braintreeClient,
  onDepositing,
}: Props) {
  const { depositMutation } = useDeposit<CreditCardDepositRequest>({ braintreeClient })
  const flow = useSelector(selectDepositFlow)
  const hostApi = useHostContext()

  const {
    data: userData,
    isError: isUserDataError,
    isSuccess: isUserDataSuccess,
  } = useUserContactDetails(accountNumber)

  useEffect(() => {
    if (isUserDataError) {
      track('deposit-3d-secure-user-data-fail', {
        accountNumber,
        depositVariant: flow,
      })
    }
  }, [accountNumber, flow, isUserDataError])

  useEffect(() => {
    if (isUserDataSuccess) {
      track('deposit-3d-secure-user-data-success', {
        accountNumber,
        depositVariant: flow,
      })
    }
  }, [accountNumber, flow, isUserDataSuccess])

  const deposit = async ({ creditCard, depositAmount }: DepositProps): Promise<DepositResponse> => {
    const client = braintreeClient.current

    if (!client) {
      // TODO: Move error capture to callsite
      const message = 'Error Braintree client is uninitialized'
      reportErrorIfNeeded({
        message,
      })

      throw new Error(message)
    }

    const response = await prepareDeposit({
      accountNumber,
      creditCard,
      client,
      mustUse3DSecure,
      depositAmount,
      initialData,
      userData,
      hostApi,
      flow,
    })

    if (response.wasUserCanceled) {
      throw DepositError.canceled(initialData.transactionId)
    }

    onDepositing()

    const { paymentMethodToken, paymentMethodNonce } = response

    return depositMutation.mutateAsync({
      amount: depositAmount,
      transactionId: initialData.transactionId,
      paymentMethodNonce,
      paymentMethodToken,
      makeDefault: false,
      forceThreeDSecureClient: false,
      depositSource: 'ExistingPaymentMethod',
    })
  }

  return {
    deposit,
  }
}

async function prepareDeposit({
  accountNumber,
  creditCard,
  client,
  initialData: { transactionId },
  mustUse3DSecure,
  depositAmount,
  userData,
  hostApi,
  flow,
}: DepositParams): Promise<UserCancelable<PrepareDepositResult>> {
  const { token: paymentMethodToken } = creditCard

  const { isSuccess, nonce } = await generatePaymentNonce(
    paymentMethodToken,
    depositAmount,
    transactionId
  )

  if (!isSuccess) {
    throw new DepositError(
      'unable_to_retrieve_tokens',
      transactionId,
      undefined,
      'Failed to retrieve tokens'
    )
  }

  const paymentTokenResult = await getPaymentTokens({
    nonce,
    accountNumber,
    client,
    mustUse3DSecure,
    creditCard,
    depositAmount,
    userData,
    transactionId,
    hostApi,
    flow,
  })

  if (paymentTokenResult.wasUserCanceled) {
    return paymentTokenResult
  }

  const { nonce: paymentMethodNonce, liabilityShiftStatus } = paymentTokenResult

  if (liabilityShiftStatus !== 'did_not_ask' && liabilityShiftStatus !== 'shifted') {
    throw new DepositError(
      'card_verification_failed',
      transactionId,
      undefined,
      'Card verification failed'
    )
  }

  return {
    wasUserCanceled: false,
    paymentMethodNonce,
    paymentMethodToken,
  }
}

async function getPaymentTokens({
  nonce,
  accountNumber,
  client,
  mustUse3DSecure,
  creditCard,
  depositAmount,
  userData,
  transactionId,
  flow,
}: PaymentTokenParams): Promise<UserCancelable<PaymentTokenResult>> {
  const paymentMethod: PaymentMethods = 'CreditCard'

  if (!mustUse3DSecure) {
    track('deposit-non-3d-secure', {
      accountNumber,
      paymentMethod,
      depositVariant: flow,
    })

    addCrumb(
      'deposit',
      'user account to not get verified with 3ds as it has been turned off in Goldmine'
    )

    return {
      wasUserCanceled: false,
      nonce,
      liabilityShiftStatus: 'did_not_ask',
    }
  }

  try {
    const threeDSecure = await verifyThreeDSecure(client, {
      nonce,
      bin: creditCard.bin ?? creditCard.number.slice(0, 6),
      depositAmount,
      userData,
    })

    if (threeDSecure.threeDSecureInfo.liabilityShifted) {
      logLiabilityShiftSuccess({
        accountNumber,
        transactionId,
        paymentMethod,
        flow,
      })

      return {
        wasUserCanceled: false,
        nonce: threeDSecure.nonce,
        liabilityShiftStatus: 'shifted',
      }
    } else {
      logLiabilityShiftRejected({
        accountNumber,
        transactionId,
        paymentMethod,
        flow,
        response: {
          wasUserCanceled: false,
          nonce: threeDSecure.nonce,
          threeDSecureInfo: threeDSecure.threeDSecureInfo,
        },
      })

      return {
        wasUserCanceled: false,
        nonce: threeDSecure.nonce,
        liabilityShiftStatus: 'not_shifted',
      }
    }
  } catch (error) {
    if (error instanceof ThreeDSecureError && error.code === 'user_canceled') {
      addCrumb(
        'deposit',
        'user has cancelled 3ds card verification step therefore deposit was not made'
      )

      return {
        wasUserCanceled: true,
      }
    }

    logLiabilityShiftError({
      accountNumber,
      transactionId,
      errorMessage: unwrapErrorMessage(coerceIntoError(error)),
      paymentMethod,
      flow,
    })

    return {
      wasUserCanceled: false,
      nonce: '',
      liabilityShiftStatus: 'verification_failed',
    }
  }
}

// region Types
type PaymentTokenParams = {
  client: Client
  accountNumber: number
  nonce: string
  mustUse3DSecure: boolean
  creditCard: PaymentMethod
  depositAmount: number
  transactionId: string
  userData?: ContactDetails
  hostApi: HostApi
  flow: DepositFlow
}

type DepositParams = {
  accountNumber: number
  client: Client
  initialData: InitialData
  creditCard: PaymentMethod
  depositAmount: number
  mustUse3DSecure: boolean
  userData?: ContactDetails
  hostApi: HostApi
  flow: DepositFlow
}

type PaymentTokenResult = {
  nonce: string
  liabilityShiftStatus: 'shifted' | 'not_shifted' | 'did_not_ask' | 'verification_failed'
}

type PrepareDepositResult = {
  paymentMethodNonce: string
  paymentMethodToken: string
}
// endregion
